import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { ContentBlock, ListItem, Offer, Settings } from '@/types/content'
import type { InquiryErrors, InquiryField } from '@/lib/inquiry'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowUpRight, Check, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cms, hasValue, slugify } from '@/lib/cms'

import {
    INQUIRY_LIMITS,
    UNDECIDED_OPTION,
    normalizeInquiry,
    validateInquiry
} from '@/lib/inquiry'

interface InquiryFormProps {
    settings: Settings
    offers: Array<Offer>
    goals: Array<ListItem>
    success?: ContentBlock
}

interface FormValues {
    vorname: string
    email: string
    telefon: string
    coachingArt: string
    ziele: Array<string>
    nachricht: string
    datenschutz: boolean
    website: string
}

type Status = 'idle' | 'sending' | 'success' | 'error'

const FIELD_ORDER: Array<InquiryField> = ['vorname', 'email', 'telefon', 'coachingArt', 'ziele', 'nachricht', 'datenschutz']

const fieldId = (field: InquiryField): string => `anfrage-${field}`

const inputClass = 'h-13 w-full rounded-[var(--radius)] border border-sand bg-bone/50 px-4 text-base text-ink placeholder:text-smoke/70 transition-[border-color,box-shadow,background-color] duration-300 hover:border-ink/40 focus:border-ink focus:bg-paper focus:outline-none focus:ring-2 focus:ring-ember/25 aria-[invalid=true]:border-destructive'

interface FieldErrorProps {
    field: InquiryField
    errors: InquiryErrors
}

const FieldError: FC<FieldErrorProps> = ({ field, errors }): ReactNode => {
    if (!errors[field]) return null

    return (
        <p id={`${fieldId(field)}-fehler`} className='text-sm text-destructive'>
            {errors[field]}
        </p>
    )
}

const describedBy = (field: InquiryField, errors: InquiryErrors, hint?: string): string | undefined => {
    const ids = [hint, errors[field] ? `${fieldId(field)}-fehler` : undefined].filter(Boolean)

    return ids.length > 0 ? ids.join(' ') : undefined
}

// The request form for an initial consultation. Validates like the server does, stores the request
// through /api/anfrage and only shows the confirmation once the server has accepted it.
export const InquiryForm: FC<InquiryFormProps> = ({ settings, offers, goals, success }): ReactNode => {
    const router = useRouter()
    const startedAt = useRef(0)
    const successRef = useRef<HTMLHeadingElement>(null)
    const [status, setStatus] = useState<Status>('idle')
    const [serverMessage, setServerMessage] = useState('')
    const [errors, setErrors] = useState<InquiryErrors>({})

    const [values, setValues] = useState<FormValues>({
        vorname: '',
        email: '',
        telefon: '',
        coachingArt: '',
        ziele: [],
        nachricht: '',
        datenschutz: false,
        website: ''
    })

    const coachingOptions = [...offers.map(offer => ({ value: offer.name, offer })), { value: UNDECIDED_OPTION, offer: null }]

    useEffect(() => {
        startedAt.current = Date.now()
    }, [])

    // Preselect from links such as /kontakt?angebot=online-coaching or /kontakt?ziel=muskelaufbau
    useEffect(() => {
        if (!router.isReady) return

        const offerSlug = typeof router.query.angebot === 'string' ? router.query.angebot : ''
        const goalSlug = typeof router.query.ziel === 'string' ? router.query.ziel : ''
        const offer = offers.find(entry => entry.slug === offerSlug)
        const goal = goals.find(entry => slugify(entry.titel) === goalSlug)

        if (!offer && !goal) return

        setValues(current => ({
            ...current,
            coachingArt: offer ? offer.name : current.coachingArt,
            ziele: goal && !current.ziele.includes(goal.titel) ? [...current.ziele, goal.titel] : current.ziele
        }))
    }, [router.isReady, router.query.angebot, router.query.ziel, offers, goals])

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    const update = (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.type === 'checkbox' ? (event.target as HTMLInputElement).checked : event.target.value

        setValues(current => ({ ...current, [field]: value }))

        if (field in errors) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const toggleGoal = (title: string) => {
        setValues(current => ({
            ...current,
            ziele: current.ziele.includes(title) ? current.ziele.filter(entry => entry !== title) : [...current.ziele, title]
        }))
    }

    const focusFirstError = (found: InquiryErrors) => {
        const first = FIELD_ORDER.find(field => found[field])

        if (!first) return

        const target = document.getElementById(fieldId(first)) ?? document.querySelector<HTMLElement>(`[name="${first}"]`)

        target?.focus()
    }

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'sending') return

        const input = normalizeInquiry({ ...values, startedAt: startedAt.current })
        const found = validateInquiry(input)

        setErrors(found)

        if (Object.keys(found).length > 0) {
            focusFirstError(found)

            return
        }

        setStatus('sending')
        setServerMessage('')

        try {
            const response = await fetch('/api/anfrage', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(input)
            })

            const data = await response.json().catch(() => null) as { message?: string, errors?: InquiryErrors } | null

            if (response.ok) {
                setStatus('success')

                return
            }

            if (response.status === 400 && data?.errors) {
                setErrors(data.errors)
                setStatus('idle')
                focusFirstError(data.errors)

                return
            }

            setServerMessage(data?.message ?? 'Deine Anfrage konnte gerade nicht übermittelt werden. Bitte versuche es in einem Moment noch einmal.')
            setStatus('error')
        }

        catch {
            setServerMessage('Die Verbindung ist gerade unterbrochen. Bitte prüfe deine Internetverbindung und versuche es noch einmal.')
            setStatus('error')
        }
    }

    if (status === 'success') {
        return (
            <div role='status' className='flex flex-col items-start gap-6 animate-in fade-in slide-in-from-bottom-2 duration-700'>
                <span aria-hidden='true' className='flex size-14 items-center justify-center rounded-full bg-ember text-paper'>
                    <Check className='size-6' strokeWidth={2.25} />
                </span>

                {success && hasValue(success.ueberschrift) && (
                    <h2
                        ref={successRef}
                        tabIndex={-1}
                        data-mdlfy-cms={cms('inhalte', success.id, 'ueberschrift')}
                        className='type-display text-display-md outline-none'
                    >
                        {success.ueberschrift}
                    </h2>
                )}

                {success && hasValue(success.text) && (
                    <p data-mdlfy-cms={cms('inhalte', success.id, 'text')} className='type-lead max-w-lg text-smoke'>
                        {success.text}
                    </p>
                )}

                <Link href='/' className='group inline-flex items-center gap-2 font-medium text-ink transition-colors duration-300 hover:text-ember'>
                    <span className='link-underline'>Zurück zur Startseite</span>
                    <ArrowUpRight aria-hidden='true' className='size-4 transition-transform duration-500 ease-brand group-hover:rotate-45' />
                </Link>
            </div>
        )
    }

    const sending = status === 'sending'

    return (
        <form noValidate onSubmit={submit} aria-describedby='anfrage-hinweis' className='flex flex-col gap-8'>
            <div className='flex flex-col gap-2'>
                <h2 className='type-display text-display-sm'>
                    Deine Anfrage
                </h2>

                <p id='anfrage-hinweis' className='text-sm text-smoke'>
                    Felder mit * sind Pflichtfelder. Das Ausfüllen dauert etwa eine Minute.
                </p>
            </div>

            <div className='grid gap-6 sm:grid-cols-2'>
                <div className='flex flex-col gap-2'>
                    <label htmlFor={fieldId('vorname')} className='text-sm font-medium text-ink'>
                        Vorname *
                    </label>

                    <input
                        id={fieldId('vorname')}
                        name='vorname'
                        type='text'
                        autoComplete='given-name'
                        required
                        maxLength={INQUIRY_LIMITS.vorname}
                        value={values.vorname}
                        onChange={update('vorname')}
                        aria-invalid={Boolean(errors.vorname)}
                        aria-describedby={describedBy('vorname', errors)}
                        className={inputClass}
                    />

                    <FieldError field='vorname' errors={errors} />
                </div>

                <div className='flex flex-col gap-2'>
                    <label htmlFor={fieldId('email')} className='text-sm font-medium text-ink'>
                        E-Mail-Adresse *
                    </label>

                    <input
                        id={fieldId('email')}
                        name='email'
                        type='email'
                        inputMode='email'
                        autoComplete='email'
                        required
                        maxLength={INQUIRY_LIMITS.email}
                        value={values.email}
                        onChange={update('email')}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={describedBy('email', errors)}
                        className={inputClass}
                    />

                    <FieldError field='email' errors={errors} />
                </div>

                <div className='flex flex-col gap-2 sm:col-span-2'>
                    <label htmlFor={fieldId('telefon')} className='text-sm font-medium text-ink'>
                        Telefonnummer
                        <span className='font-normal text-smoke'> (optional)</span>
                    </label>

                    <input
                        id={fieldId('telefon')}
                        name='telefon'
                        type='tel'
                        inputMode='tel'
                        autoComplete='tel'
                        maxLength={INQUIRY_LIMITS.telefon}
                        value={values.telefon}
                        onChange={update('telefon')}
                        aria-invalid={Boolean(errors.telefon)}
                        aria-describedby={describedBy('telefon', errors, 'anfrage-telefon-hinweis')}
                        className={cn(inputClass, 'sm:max-w-sm')}
                    />

                    <p id='anfrage-telefon-hinweis' className='text-sm text-smoke'>
                        Wenn du lieber angerufen werden möchtest.
                    </p>

                    <FieldError field='telefon' errors={errors} />
                </div>
            </div>

            <fieldset
                id={fieldId('coachingArt')}
                tabIndex={-1}
                aria-invalid={Boolean(errors.coachingArt)}
                aria-describedby={describedBy('coachingArt', errors)}
                className='flex flex-col gap-3 outline-none'
            >
                <legend className='mb-3 text-sm font-medium text-ink'>
                    Welche Betreuung interessiert dich? *
                </legend>

                <div className='grid gap-3 sm:grid-cols-2'>
                    {coachingOptions.map(option => {
                        const checked = values.coachingArt === option.value

                        return (
                            <label key={option.value} className='relative flex cursor-pointer'>
                                <input
                                    type='radio'
                                    name='coachingArt'
                                    value={option.value}
                                    checked={checked}
                                    onChange={update('coachingArt')}
                                    className='peer sr-only'
                                />

                                <span
                                    className={cn(
                                        'flex w-full items-center justify-between gap-4 rounded-[var(--radius)] border px-4 py-3.5 text-[0.9375rem] font-medium transition-[border-color,background-color,color] duration-300 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ember',
                                        checked ? 'border-ink bg-ink text-bone' : 'border-sand bg-bone/50 text-ink hover:border-ink/40',
                                        errors.coachingArt && !checked && 'border-destructive/60'
                                    )}
                                >
                                    {option.offer
                                        ? <span data-mdlfy-cms={cms('angebote', option.offer.id, 'name')}>{option.offer.name}</span>
                                        : <span>{option.value}</span>}

                                    <span
                                        aria-hidden='true'
                                        className={cn('flex size-5 shrink-0 items-center justify-center rounded-full border', checked ? 'border-ember bg-ember text-paper' : 'border-sand')}
                                    >
                                        {checked && <Check className='size-3' strokeWidth={3} />}
                                    </span>
                                </span>
                            </label>
                        )
                    })}
                </div>

                <FieldError field='coachingArt' errors={errors} />
            </fieldset>

            {goals.length > 0 && (
                <fieldset
                    id={fieldId('ziele')}
                    tabIndex={-1}
                    aria-describedby={describedBy('ziele', errors)}
                    className='flex flex-col gap-3 outline-none'
                >
                    <legend className='mb-3 text-sm font-medium text-ink'>
                        Was möchtest du erreichen?
                        <span className='font-normal text-smoke'> (optional, mehrere möglich)</span>
                    </legend>

                    <div className='flex flex-wrap gap-2'>
                        {goals.map(goal => {
                            const checked = values.ziele.includes(goal.titel)

                            return (
                                <label key={goal.id} className='relative flex cursor-pointer'>
                                    <input
                                        type='checkbox'
                                        name='ziele'
                                        value={goal.titel}
                                        checked={checked}
                                        onChange={() => toggleGoal(goal.titel)}
                                        className='peer sr-only'
                                    />

                                    <span
                                        className={cn(
                                            'inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-[border-color,background-color,color] duration-300 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ember',
                                            checked ? 'border-ink bg-ink text-bone' : 'border-sand bg-bone/50 text-ink hover:border-ink/40'
                                        )}
                                    >
                                        {checked && <Check aria-hidden='true' className='size-3.5 text-ember-bright' strokeWidth={3} />}
                                        <span data-mdlfy-cms={cms('listenpunkte', goal.id, 'titel')}>{goal.titel}</span>
                                    </span>
                                </label>
                            )
                        })}
                    </div>

                    <FieldError field='ziele' errors={errors} />
                </fieldset>
            )}

            <div className='flex flex-col gap-2'>
                <div className='flex items-baseline justify-between gap-4'>
                    <label htmlFor={fieldId('nachricht')} className='text-sm font-medium text-ink'>
                        Nachricht
                        <span className='font-normal text-smoke'> (optional)</span>
                    </label>

                    <span aria-hidden='true' className='type-label text-smoke'>
                        {values.nachricht.length} / {INQUIRY_LIMITS.nachricht}
                    </span>
                </div>

                <textarea
                    id={fieldId('nachricht')}
                    name='nachricht'
                    rows={5}
                    maxLength={INQUIRY_LIMITS.nachricht}
                    value={values.nachricht}
                    onChange={update('nachricht')}
                    aria-invalid={Boolean(errors.nachricht)}
                    aria-describedby={describedBy('nachricht', errors, 'anfrage-nachricht-hinweis')}
                    placeholder='Erzähl kurz, wo du gerade stehst und was du dir vorstellst.'
                    className={cn(inputClass, 'h-auto min-h-36 resize-y py-3 leading-relaxed')}
                />

                <p id='anfrage-nachricht-hinweis' className='text-sm text-smoke'>
                    Bitte keine Gesundheitsdaten oder Diagnosen angeben. Das besprechen wir bei Bedarf persönlich.
                </p>

                <FieldError field='nachricht' errors={errors} />
            </div>

            <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                <label htmlFor='anfrage-website'>Webseite</label>
                <input id='anfrage-website' name='website' type='text' tabIndex={-1} autoComplete='off' value={values.website} onChange={update('website')} />
            </div>

            <div className='flex flex-col gap-2'>
                <label className='flex cursor-pointer items-start gap-3'>
                    <input
                        id={fieldId('datenschutz')}
                        name='datenschutz'
                        type='checkbox'
                        checked={values.datenschutz}
                        onChange={update('datenschutz')}
                        aria-invalid={Boolean(errors.datenschutz)}
                        aria-describedby={describedBy('datenschutz', errors)}
                        className='mt-0.5 size-5 shrink-0 cursor-pointer accent-ink'
                    />

                    <span className='text-sm leading-relaxed text-smoke'>
                        Ich bin einverstanden, dass meine Angaben zur Bearbeitung meiner Anfrage gespeichert und verarbeitet werden. Mehr dazu in der{' '}
                        <Link href='/datenschutz' target='_blank' className='font-medium text-ink underline underline-offset-4 hover:text-ember'>
                            Datenschutzerklärung
                        </Link>
                        . *
                    </span>
                </label>

                <FieldError field='datenschutz' errors={errors} />
            </div>

            {status === 'error' && serverMessage && (
                <div role='alert' className='flex flex-col gap-1 rounded-[var(--radius)] border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-ink'>
                    <p>{serverMessage}</p>

                    {hasValue(settings.email) && (
                        <p className='text-smoke'>
                            <span>Alternativ erreichst du mich per E-Mail an </span>
                            <a href={`mailto:${settings.email}`} className='font-medium text-ink underline underline-offset-4'>
                                <span data-mdlfy-cms={cms('einstellungen', settings.id, 'email')}>{settings.email}</span>
                            </a>
                            <span>.</span>
                        </p>
                    )}
                </div>
            )}

            <div className='flex flex-col items-start gap-4 border-t border-ink/10 pt-8'>
                <button
                    type='submit'
                    disabled={sending}
                    className='group inline-flex h-14 items-center justify-between gap-5 rounded-full bg-ink pl-7 pr-2 text-base font-medium text-bone transition-colors duration-300 ease-brand hover:bg-ember-deep disabled:cursor-wait disabled:opacity-80'
                >
                    {sending
                        ? <span>Wird gesendet …</span>
                        : <span data-mdlfy-cms={cms('einstellungen', settings.id, 'cta_text')}>{settings.cta_text}</span>}

                    <span aria-hidden='true' className='flex size-10 items-center justify-center rounded-full bg-ember text-paper transition-[background-color,color,transform] duration-500 ease-brand group-hover:rotate-45 group-hover:bg-bone group-hover:text-ink'>
                        {sending
                            ? <LoaderCircle className='size-4 animate-spin' />
                            : <ArrowUpRight className='size-4' strokeWidth={2.25} />}
                    </span>
                </button>
            </div>
        </form>
    )
}
